import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useBookDocs, useUpdateBookDocs } from '@/hooks/useBookDocs'
import { downloadExport } from '@/lib/api'

function Field({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <span className="mt-0.5 block text-xs text-slate-400">{hint}</span>
      <textarea
        className="mt-1 w-full rounded-lg border border-slate-300 p-2 font-mono text-sm"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

export default function BookSettings() {
  const { slug = '' } = useParams()
  const { data: book } = useBookDocs(slug)
  const update = useUpdateBookDocs(slug)
  const [glossary, setGlossary] = useState('')
  const [rules, setRules] = useState('')
  const [style, setStyle] = useState('')
  const [narrationStyle, setNarrationStyle] = useState('')
  const [pronunciation, setPronunciation] = useState('')

  useEffect(() => {
    if (!book) return
    setGlossary(book.glossaryMd ?? '')
    setRules(book.rulesMd ?? '')
    setStyle(book.styleMd ?? '')
    setNarrationStyle(book.narrationStyle ?? '')
    setPronunciation(book.pronunciationMd ?? '')
  }, [book])

  if (!book) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <Link to={`/books/${slug}`} className="text-slate-500 hover:text-slate-800">←</Link>
        <h1 className="text-lg font-semibold text-slate-900">{book.title} · Translation</h1>
      </header>

      <main className="mx-auto max-w-2xl space-y-5 px-4 py-6">
        <p className="text-xs text-slate-400">
          These override your shared language-pair defaults for this book and steer the AI translator.
        </p>
        <Field label="Glossary" hint="Fixed term translations: `term — translation`, one per line."
               value={glossary} onChange={setGlossary} />
        <Field label="Rules" hint="Hard rules and conventions." value={rules} onChange={setRules} />
        <Field label="Style" hint="Voice, tone and register." value={style} onChange={setStyle} />

        <section className="border-t border-slate-200 pt-5">
          <h2 className="text-sm font-medium text-slate-800">Narration (audio)</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            How the Listen-mode voice reads this book. Changing these re-generates affected audio on the next sync.
          </p>

          <div className="mt-4">
            <Field
              label="Narration style"
              hint="Natural-language voice direction, e.g. “Warm, unhurried narrator; slightly dramatic on dialogue.”"
              value={narrationStyle}
              onChange={setNarrationStyle}
            />
          </div>
          <div className="mt-4">
            <Field
              label="Pronunciation"
              hint="Respellings for tricky words: `word — spoken form`, one per line (e.g. `Eden — Y-den`)."
              value={pronunciation}
              onChange={setPronunciation}
            />
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              update.mutate({
                bookId: book.id,
                glossaryMd: glossary,
                rulesMd: rules,
                styleMd: style,
                narrationStyle,
                pronunciationMd: pronunciation,
              })
            }
            disabled={update.isPending}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          {update.isSuccess && <span className="text-sm text-emerald-600">Saved.</span>}
          {update.isError && <span className="text-sm text-red-600">{(update.error as Error).message}</span>}
        </div>

        <section className="border-t border-slate-200 pt-5">
          <h2 className="text-sm font-medium text-slate-800">Export</h2>
          <div className="mt-2 flex gap-3">
            <button
              onClick={() => downloadExport(slug, 'translated')}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm hover:bg-slate-50"
            >
              Download translated EPUB
            </button>
            <button
              onClick={() => downloadExport(slug, 'original')}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm hover:bg-slate-50"
            >
              Download original EPUB
            </button>
          </div>
        </section>
      </main>
    </div>
  )
}
